/*
 * Copyright (c) 2022 automata-network/tgb-donation authors and contributors.
 * This software may be modified and distributed under the terms of MIT license.
 * Refer to the LICENSE file for details.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'

import { useMutation } from 'react-query'
import { toast } from 'react-toastify'
import styled, { css } from 'styled-components'

import { PrimaryButton } from 'src/components/Button'
import FlexBox from 'src/components/Common/FlexBox'
import { ToastContent } from 'src/components/Common/Toast'
import { DonateMethodDialog } from 'src/components/Modal'
import { ERROR_MESSAGE } from 'src/constants/errors'
import { useModalContext } from 'src/contexts/ModalContext'
import { pxToVw } from 'src/functions/units'

import {
  useDonationIdentityCollectorContext,
  useDonorIdentityCollectorModal
} from '../donation-identity-collector.hooks'
import {
  DonorIdentity,
  DonorIdentityMutationResponse,
  postDonorIdentityRequest
} from '../donation-identity-collector.services'
import { IdentityInputField } from './IdentityInputField'

// Accept name consists of case-sensitive latin chars, including the char with an accent.
// Each word before the last one can be separated using either dash (-) or whitespace.
// e.g. Mohamad Irviné Abraham Nür-rasyid and Alaya-bàrry
const latinCharsRange = 'a-zA-Z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u024F'
const donorNameRegex = new RegExp(
  `^(?:(?:[${latinCharsRange}]+[ ]?)|(?:[${latinCharsRange}]+-)){1,}[${latinCharsRange}]+$`
)

// Accept only regular chars, number, dash and space for identity card number
const icNumberRegex = /^[a-z\d-\s]+$/i

// Accept simple combination of email address
const emailRegex = /^[a-z\d._%+-]+@[a-z\d.-]+\.[a-z]{2,10}$/i

// Accept numbers only or numbers with prefixed international call code.
// e.g 0819754321 or +62819754321
const phoneNumberRegex = /^\+?\d+$/

export const IdentityCollectorForm = () => {
  const { donorIdentity, setDonorIdentity, setDonorIdentityId, storePersistentDonorIdentityData } =
    useDonationIdentityCollectorContext()
  const { openModal } = useModalContext()
  const { closeCollectorModal } = useDonorIdentityCollectorModal()

  // const [donorType, setDonorType] = useState<DonorIdentityType>('national_id')
  const [checkboxesState, setCheckboxesState] = useState<boolean[]>(new Array(2).fill(false))
  const [errorsState, setErrorsState] = useState<boolean[]>(new Array(4).fill(false))
  const [fieldInitState, setFieldInitState] = useState<boolean[]>(new Array(5).fill(true))

  const mutation = useMutation<DonorIdentityMutationResponse, unknown, DonorIdentity>(
    'fiat-customer-profile',
    async (requestBody) => {
      try {
        const response = await postDonorIdentityRequest(requestBody)
        return response.json()
      } catch (e: any) {
        throw new Error(e)
      }
    }
  )

  useEffect(() => {
    if (fieldInitState.every((init) => init === true) && donorIdentity) {
      const { email, ic_number, name, phone_number, type } = donorIdentity
      const _fieldInitState: boolean[] = [!type, !name, !ic_number, !email, !phone_number]
      setFieldInitState(_fieldInitState)
    }
  }, [donorIdentity, fieldInitState])

  const submitButtonIsDisabled = useMemo(
    () =>
      checkboxesState.some((checked) => checked === false) ||
      errorsState.some((error) => error === true) ||
      fieldInitState.some((init) => init === true) ||
      mutation.isLoading,
    [checkboxesState, errorsState, fieldInitState, mutation.isLoading]
  )

  const handleSubmit = useCallback(async () => {
    if (submitButtonIsDisabled) return

    try {
      const mutationResult = await mutation.mutateAsync(donorIdentity as DonorIdentity)
      setDonorIdentityId(mutationResult.id)
      storePersistentDonorIdentityData(mutationResult.id)
      closeCollectorModal()
      openModal(<DonateMethodDialog />, 'donate-method', {
        dialogTitle: 'Choose donation method'
      })
    } catch (e) {
      return toast.error(<ToastContent message={ERROR_MESSAGE.DONOR_POST_API_FAILED} />)
    }
  }, [
    closeCollectorModal,
    donorIdentity,
    mutation,
    openModal,
    setDonorIdentityId,
    storePersistentDonorIdentityData,
    submitButtonIsDisabled
  ])

  const handleInputFieldChange = useCallback(
    (field: keyof DonorIdentity, value: string) => {
      const initStateKey =
        field === 'name' ? 1 : field === 'ic_number' ? 2 : field === 'email' ? 3 : 4
      if (fieldInitState[initStateKey] === true) {
        const _fieldInitState = fieldInitState
        _fieldInitState.splice(initStateKey, 1, false)
        setFieldInitState(_fieldInitState)
      }

      setDonorIdentity({
        [field]: value
      })
    },
    [fieldInitState, setDonorIdentity]
  )

  return (
    <FlexBox flexDirection="column" rowGap={24}>
      <StyledRadioGroupSet>
        <div>Donor Type</div>
        <FlexBox columnGap={16}>
          <FlexBox as="label" alignItems="center" columnGap={6}>
            <input
              type="radio"
              name="donorType"
              value="national_id"
              checked={
                !!(
                  (donorIdentity && donorIdentity.type === 'national_id') ||
                  !donorIdentity ||
                  !donorIdentity.type
                )
              }
              onChange={() =>
                setDonorIdentity({
                  type: 'national_id'
                })
              }
            />
            Individual
          </FlexBox>

          <FlexBox as="label" alignItems="center" columnGap={6}>
            <input
              type="radio"
              name="donorType"
              value="uen"
              checked={!!(donorIdentity && donorIdentity.type === 'uen')}
              onChange={() =>
                setDonorIdentity({
                  type: 'uen'
                })
              }
            />
            Company
          </FlexBox>
        </FlexBox>
      </StyledRadioGroupSet>

      <StyledFieldSet
        label={
          (donorIdentity && donorIdentity.type === 'national_id') ||
          !donorIdentity ||
          !donorIdentity.type
            ? 'Name'
            : 'Company Name'
        }
        minLength={4}
        maxLength={100}
        required
        rule={donorNameRegex}
        ruleErrorMessage={ERROR_MESSAGE.IDENTITY_NAME_INVALID_FORMAT}
        value={donorIdentity?.name}
        help="* Ensure the name is the same on your Identity Card."
        onChange={(value) => handleInputFieldChange('name', value)}
        onError={(errorMessage) =>
          setErrorsState((prev) => [!!errorMessage, prev[1], prev[2], prev[3]])
        }
      />

      <StyledFieldSet
        label={
          (donorIdentity && donorIdentity.type === 'national_id') ||
          !donorIdentity ||
          !donorIdentity.type
            ? 'Identity Card Number'
            : 'Unique Entity Number (UEN)'
        }
        minLength={
          (donorIdentity && donorIdentity.type === 'national_id') ||
          !donorIdentity ||
          !donorIdentity.type
            ? 4
            : 9
        }
        maxLength={
          (donorIdentity && donorIdentity.type === 'national_id') ||
          !donorIdentity ||
          !donorIdentity.type
            ? 20
            : 10
        }
        required
        rule={icNumberRegex}
        ruleErrorMessage={ERROR_MESSAGE.IDENTITY_CARD_NUMBER_INVALID_FORMAT}
        value={donorIdentity?.ic_number}
        onChange={(value) => handleInputFieldChange('ic_number', value)}
        onError={(errorMessage) =>
          setErrorsState((prev) => [prev[0], !!errorMessage, prev[2], prev[3]])
        }
      />

      <StyledFieldSet
        label="Email Address"
        minLength={5}
        required
        rule={emailRegex}
        ruleErrorMessage={ERROR_MESSAGE.IDENTITY_EMAIL_INVALID_FORMAT}
        type="email"
        value={donorIdentity?.email}
        onChange={(value) => handleInputFieldChange('email', value)}
        onError={(errorMessage) =>
          setErrorsState((prev) => [prev[0], prev[1], !!errorMessage, prev[3]])
        }
      />

      <StyledFieldSet
        label="Phone Number"
        minLength={5}
        maxLength={24}
        required
        rule={phoneNumberRegex}
        ruleErrorMessage={ERROR_MESSAGE.IDENTITY_PHONE_INVALID_FORMAT}
        type="text"
        value={donorIdentity?.phone_number}
        onChange={(value) => handleInputFieldChange('phone_number', value)}
        onError={(errorMessage) =>
          setErrorsState((prev) => [prev[0], prev[1], prev[2], !!errorMessage])
        }
      />

      <FlexBox flexDirection="column" rowGap={12}>
        <StyledCheckboxWrap forwardedAs="label" columnGap={10}>
          <StyledCheckbox
            type="checkbox"
            checked={checkboxesState[0]}
            onChange={() => {
              setCheckboxesState((prev) => [!prev[0], prev[1]])
            }}
          />
          <span>
            I confirm that the information above is my <strong>real identity</strong> as stated on
            my valid{' '}
            {(donorIdentity && donorIdentity.type === 'national_id') ||
            !donorIdentity ||
            !donorIdentity.type
              ? 'national identity card'
              : 'entity registration'}
            .
          </span>
        </StyledCheckboxWrap>

        <StyledCheckboxWrap forwardedAs="label" columnGap={10}>
          <StyledCheckbox
            type="checkbox"
            checked={checkboxesState[1]}
            onChange={() => {
              setCheckboxesState((prev) => [prev[0], !prev[1]])
            }}
          />
          <span>
            I agree to use this identity as proof to <strong>receive tax deduction</strong> for this
            donation.
          </span>
        </StyledCheckboxWrap>
      </FlexBox>

      <StyledPrimaryButton
        size="large"
        borderRadius="medium"
        disabled={submitButtonIsDisabled}
        onClick={handleSubmit}
      >
        {mutation.isLoading ? 'Loading...' : 'Submit My Identity'}
      </StyledPrimaryButton>
    </FlexBox>
  )
}

const StyledRadioGroupSet = styled.div`
  > div {
    margin-bottom: ${pxToVw(7)};

    ${({ theme }) => css`
      font-size: ${pxToVw(theme.units.fontSize.body2)};
      color: ${theme.colors.gray700};

      ${theme.mediaQuery.mobile`
        font-size: ${pxToVw(theme.units.fontSize.body2, 720)};
        margin-bottom: ${pxToVw(7, 720)};
      `}
    `}
  }

  input[type='radio'] {
    margin: 3px;
  }
`

const StyledFieldSet = styled(IdentityInputField)`
  width: 100%;
`

const StyledCheckboxWrap = styled(FlexBox)`
  user-select: none;
  cursor: pointer;

  ${({ theme }) => css`
    font-size: ${pxToVw(theme.units.fontSize.body2)};
    line-height: 1.1425em;
    color: ${theme.colors.gray700};

    ${theme.mediaQuery.mobile`
      font-size: ${pxToVw(theme.units.fontSize.body2, 720)};
    `}
  `}
`

const StyledCheckbox = styled.input`
  appearance: none;
  background: ${({ theme }) => theme.colors.baseWhite};
  border: solid 2px ${({ theme }) => theme.colors.gray};
  width: ${pxToVw(16)};
  min-width: 16px;
  height: ${pxToVw(16)};
  min-height: 16px;
  flex-shrink: 0;
  border-radius: ${pxToVw(3)};
  margin: 0;
  margin-top: ${pxToVw(1)};
  cursor: pointer;

  &:checked {
    position: relative;
    background-color: ${({ theme }) => theme.colors.primary};
    border-color: ${({ theme }) => theme.colors.primary};

    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: calc(100% - ${pxToVw(2.5)});
      height: calc(100% - ${pxToVw(2.5)});
      background-image: url('data:image/svg+xml;base64,PHN2ZyBlbmFibGUtYmFja2dyb3VuZD0ibmV3IDAgMCA1MDcuNTA2IDUwNy41MDYiIGZpbGw9IiNmZmYiIGhlaWdodD0iNTEyIiB2aWV3Qm94PSIwIDAgNTA3LjUwNiA1MDcuNTA2IiB3aWR0aD0iNTEyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxwYXRoIGQ9Im0xNjMuODY1IDQzNi45MzRjLTE0LjQwNi4wMDYtMjguMjIyLTUuNzItMzguNC0xNS45MTVsLTExNi4wOTYtMTE2LjA1M2MtMTIuNDkyLTEyLjQ5Ni0xMi40OTItMzIuNzUyIDAtNDUuMjQ4IDEyLjQ5Ni0xMi40OTIgMzIuNzUyLTEyLjQ5MiA0NS4yNDggMGwxMDkuMjQ4IDEwOS4yNDggMjg5LjAyNC0yODkuMDI0YzEyLjQ5Ni0xMi40OTIgMzIuNzUyLTEyLjQ5MiA0NS4yNDggMCAxMi40OTIgMTIuNDk2IDEyLjQ5MiAzMi43NTIgMCA0NS4yNDhsLTI5NS44NzIgMjk1LjgyOWMtMTAuMTc4IDEwLjE5NS0yMy45OTQgMTUuOTIxLTM4LjQgMTUuOTE1eiIvPjwvc3ZnPg==');
      background-size: cover;
    }
  }

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      width: ${pxToVw(16, 720)};
      height: ${pxToVw(16, 720)};
      border-radius: ${pxToVw(3, 720)};
      margin-top: ${pxToVw(1, 720)};

      &:checked {
        &::after {
          width: calc(100% - ${pxToVw(2.5, 720)});
          height: calc(100% - ${pxToVw(2.5, 720)});
        }
      }
    `}
  `}
`

const StyledPrimaryButton = styled(PrimaryButton)`
  width: 100%;

  &:disabled {
    opacity: 0.5;
  }
`
